import React from 'react';
import { Card } from '../ui/Card';

export interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  subValue?: React.ReactNode;
  icon: React.ReactNode;
  iconColor?: string;
  badge?: React.ReactNode;
  trend?: {
    value: string;
    isPositive?: boolean;
  };
  highlight?: boolean;
  onClick?: () => void;
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subValue,
  icon,
  iconColor = 'text-accent-emerald',
  badge,
  trend,
  highlight = false,
  onClick,
  className = '',
}) => {
  return (
    <Card
      variant={highlight ? 'neon-border' : onClick ? 'interactive' : 'default'}
      padding="none"
      onClick={onClick}
      className={`
        relative overflow-hidden flex flex-col justify-between p-3 sm:p-3.5
        transition-all duration-200 min-h-[95px]
        ${className}
      `}
    >
      {/* Top row: Title and Icon/Badge */}
      <div className="flex items-center justify-between gap-1.5 mb-1.5">
        <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider truncate">
          {title}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {badge}
          <div
            className={`w-6 h-6 rounded-md bg-surface-hover/90 border border-white/10 flex items-center justify-center ${iconColor}`}
          >
            {icon}
          </div>
        </div>
      </div>

      {/* Center: Main metric value */}
      <div className="flex items-baseline gap-1.5 flex-wrap min-w-0 max-w-full">
        <div
          className="text-[15px] sm:text-base lg:text-lg xl:text-xl font-black font-mono text-text-primary tracking-tight leading-tight truncate max-w-full"
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </div>
        {trend && (
          <span
            className={`text-[10px] font-mono font-semibold px-1 py-0.5 rounded shrink-0 ${
              trend.isPositive !== false ? 'bg-emerald-500/15 text-accent-neon' : 'bg-rose-500/15 text-rose-400'
            }`}
          >
            {trend.value}
          </span>
        )}
      </div>

      {/* Bottom row: Sub-metrics or progress note */}
      {subValue && (
        <div className="mt-1.5 text-[10px] sm:text-[10.5px] leading-tight text-text-muted break-words">
          {subValue}
        </div>
      )}
    </Card>
  );
};
